<template>
	<div data-testid="filter" class="d-flex justify-center filter-hover">
		<div data-testid="filter__container" class="border-on d-flex shadow-on radius-small filter-toolbar gap-2">
			<!-- eslint-disable-next-line vue/no-v-for-template-key -->
			<template v-for="(header, index) in filterHeadersCompute">
				<div data-testid="filter__header" :key="header.key" class="text-center px-2 py-2 hoverable d-flex align-center justify-center">
					<v-menu data-testid="filter__menu" max-width="300" offset-y :close-on-content-click="false" rounded="lg" @input="(val) => val && setHeader(header.key)" ref="menu">
						<template v-slot:activator="{ on, attrs }">
							<div v-bind="attrs" v-on="on" class="d-flex align-center cursor-pointer gap-2">
								<span :class="isFilterActive(header) ? '' : ''" class="filter-head font-weight-medium">
									{{ header.name }}
								</span>
								<img src="/img/icons/downArrow.svg" alt="Down Arrow" small :color="isFilterActive(header) ? 'black' : 'grey lighten-1'">
							</div>
						</template>

						<!-- Date Range Filter -->
						<v-card data-testid="filter__daterange-card" v-if="header.type === 'daterange'">
							<div class="" data-testid="filter__daterange-inputs">
								<v-date-picker
									v-model="tempDateRange[header.key]"
									range
									scrollable
									color="#e1e1e1"
									:events="getTempRangeEvents(header.key)"
									event-color="#F7F7F7"
									@input="updateTempDateRange(header.key, $event)"
									control-variant="modal"
									show-adjacent-months
								></v-date-picker>
							</div>
							<v-divider data-testid="filter__daterange-divider"></v-divider>
							<v-card-actions data-testid="filter__daterange-actions" class="justify-end pa-2">
								<base-button data-testid="filter__daterange-clear-btn" secondary label="Clear" @click="clearTempSelection(header.key, 'daterange')" />
								<base-button data-testid="filter__daterange-save-btn" primary label="Apply" @click="saveSelection(header.key, 'daterange', index)" />
							</v-card-actions>
						</v-card>

						<!-- Single Select Filter -->
						<v-card data-testid="filter__singleselect-card" v-if="header.type === 'singleselect'" class="d-flex flex-column" max-height="400">
							<v-card-text class="pa-0 flex-shrink-0">
								<lb-string data-testid="filter__singleselect-search" class="px-2 pt-2 mt-0" label="Search" v-model="searchFilter[header.key]" hidedetails :isClearable="false" />
							</v-card-text>
							<v-card-text class="pa-0 ma-0 pt-2 flex-grow-1 overflow-y-auto" style="min-height: 0;">
								<v-radio-group data-testid="filter__singleselect-group" v-model="tempSelectedItems[header.key]" class="mt-0 pt-0" hide-details>
									<v-radio
										data-testid="filter__singleselect-radio"
										v-for="(option, k) in getFilterOptionsList(searchFilter[header.key], header.data, header.value).slice(0, 25)"
										:key="k"
										class="pa-2 mt-0 pt-0"
										:label="option[header.value]"
										:value="option"
									></v-radio>
								</v-radio-group>
								<div data-testid="filter__singleselect-more" v-if="getFilterOptionsList(searchFilter[header.key], header.data, header.value).length > 25" class="mx-2 my-1 grey--text">
									<span class="noTransform">{{ getFilterOptionsList(searchFilter[header.key], header.data, header.value).length - 25 }} more</span>
								</div>
							</v-card-text>
							<v-divider data-testid="filter__singleselect-divider"></v-divider>
							<v-card-actions data-testid="filter__singleselect-actions" class="justify-end pa-2 flex-shrink-0">
								<base-button data-testid="filter__singleselect-clear-btn" secondary label="Clear" @click="clearTempSelection(header.key, 'singleselect')" />
								<base-button data-testid="filter__singleselect-save-btn" primary label="Apply" @click="saveSelection(header.key, 'singleselect', index)" />
							</v-card-actions>
						</v-card>

						<!-- Multi Select Filter -->
						<v-card data-testid="filter__multiselect-card" v-else-if="header.type !== 'daterange' && header.type !== 'singleselect'" class="d-flex flex-column" max-height="450">
							<v-card-text class="pa-0 flex-shrink-0">
								<lb-string data-testid="filter__search-input" class="px-2 pt-2 mt-0" label="Search" v-model="searchFilter[header.key]" hidedetails :isClearable="false" />
							</v-card-text>

							<v-card-text class="pa-2 flex-grow-1 overflow-y-auto d-flex flex-column gap-2" style="min-height: 0;">
								<v-checkbox 
									data-testid="filter__selectall-checkbox"
									class="mt-0 pa-1 noTransform" 
									dense 
									hide-details
									label="Select All"
									:input-value="isTempSelectAll(header.key, header.data, searchFilter[header.key], header.value)"
									@change="toggleTempSelectAll(header.key, header.data, searchFilter[header.key], header.value, $event)"
								>
								</v-checkbox>
								<v-checkbox 
									data-testid="filter__option-checkbox" 
									v-for="(option, k) in getFilterOptionsList(searchFilter[header.key], header.data, header.value).slice(0, 25)" 
									:key="k" 
									class="mt-0 pa-1 noTransform" 
									dense 
									:label="option[header.value]" 
									:value="option" 
									hide-details 
									v-model="tempSelectedItems[header.key]"
								>
									<template v-slot:label>
										<span class="noTransform filter-text">{{ option[header.value] }}</span>
									</template>
								</v-checkbox>
								<div data-testid="filter__option-more" v-if="getFilterOptionsList(searchFilter[header.key], header.data, header.value).length > 25" class="mx-2 my-1 grey--text">
									<span class="noTransform">{{ getFilterOptionsList(searchFilter[header.key], header.data, header.value).length - 25 }} more</span>
								</div>
							</v-card-text>
							<v-divider data-testid="filter__multiselect-divider"></v-divider>
							<v-card-actions data-testid="filter__multiselect-actions" class="justify-end pa-2 flex-shrink-0">
								<base-button data-testid="filter__multiselect-clear-btn" secondary label="Clear" @click="clearTempSelection(header.key, 'multiselect')" />
								<base-button data-testid="filter__multiselect-save-btn" primary label="Apply" @click="saveSelection(header.key, 'multiselect', index)" />
							</v-card-actions>
						</v-card>
					</v-menu>
				</div>
				<div v-if="index < filterHeadersCompute.length" :key="header.key + '-divider'" class="d-flex align-center" style="height: 36px; align-self: center;">
					<lb-divider type="vertical"></lb-divider>
				</div>
			</template>

			<div>
				<lb-divider data-testid="filter__divider-1" type="vertical"></lb-divider>
			</div>

			<div data-testid="filter__apply-section" class="text-center hoverable" v-if="searchOption">
				<div>
					<v-tooltip data-testid="filter__apply-tooltip" bottom content-class="tooltip-bottom">
						<template v-slot:activator="{ on, attrs }">
							<div v-bind="attrs" v-on="on">
								<base-button data-testid="filter__apply-btn" id="apply" primary label="Apply" @click.stop="getFilterData()" />
							</div>
						</template>
						<span>Apply Filters</span>
					</v-tooltip>
				</div>
			</div>

			<div>
				<lb-divider data-testid="filter__divider-2" type="vertical"></lb-divider>
			</div>

			<div data-testid="filter__reset-section" class="text-center hoverable">
				<div>
					<v-tooltip data-testid="filter__reset-tooltip" bottom content-class="tooltip-bottom">
						<template v-slot:activator="{ on, attrs }">
							<div v-bind="attrs" v-on="on">
								<base-button data-testid="filter__reset-btn" icon="mdi-close" id="resetfilters" @click.stop="resetFilters()" />
							</div>
						</template>
						<span>Reset filters</span>
					</v-tooltip>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	data() {
		return {
			selectedItems: {},
			filterSearch: {},
			selectedOptions: {},
			selectedFilterItemsv2: {},
			tempSelectedItems: {}, // Temporary selection state before saving
			selectedDateRange: {},
			tempDateRange: {}, // Temporary date range before saving
			selectedSingleItem: {},
			filterheadersCopy: [],
			searchFilter: {},
			selectedHeader: '',
			tab: 0,
			selected: {},
		}
	},
	props: {
		filterheaders: {
			type: Array,
			default: () => []
		},
		loading: {
			type: Boolean,
			default: false
		},
		generateFileOption:{
			type: Boolean,
			default: false
		},
		searchOption:{
			type: Boolean,
			default: true
		},
		isFileGenarating:{
			type:Boolean,
			default: true
		},
		generateFileText : {
			type: String,
			default: 'Generate'
		},
        cleanSelectAll: {
            type: Boolean,
            default: true
        }
	},
	created() {
		if (!this.loading) {
			this.initializeFilterValues()
		}
	},
	activated() {
		if (!this.loading) {
			this.initializeFilterValues()
		}
	},
	computed: {
		filterHeadersCompute() {
			return this.filterheadersCopy
		}
	},
	methods: {
		/**
		 * Check if all visible options are selected in temp state
		 * @param {string} key - Filter header key
		 * @param {Array} data - Filter options data
		 * @param {string} searchText - Current search text
		 * @param {string} valueKey - Key to access option value
		 * @returns {boolean} - True if all visible options are selected
		 */
		isTempSelectAll(key, data, searchText, valueKey) {
			const visibleOptions = this.getFilterOptionsList(searchText, data, valueKey);
			const selected = this.tempSelectedItems[key] || [];
			if (visibleOptions.length === 0) return false;
			return visibleOptions.every(item => selected.includes(item));
		},

		/**
		 * Toggle select all for visible options in temp state
		 * @param {string} key - Filter header key
		 * @param {Array} data - Filter options data
		 * @param {string} searchText - Current search text
		 * @param {string} valueKey - Key to access option value
		 * @param {boolean} isSelected - Whether to select or deselect all
		 */
		toggleTempSelectAll(key, data, searchText, valueKey, isSelected) {
			const visibleOptions = this.getFilterOptionsList(searchText, data, valueKey);
			if (isSelected) {
				const current = this.tempSelectedItems[key] || [];
				const newItems = visibleOptions.filter(item => !current.includes(item));
				this.$set(this.tempSelectedItems, key, [...current, ...newItems]);
			} else {
				const current = this.tempSelectedItems[key] || [];
				const visibleSet = new Set(visibleOptions);
				const newSelected = current.filter(item => !visibleSet.has(item));
				this.$set(this.tempSelectedItems, key, newSelected);
			}
		},

		/**
		 * Initialize filter values and temp states
		 * @param {boolean} isFirst - Whether this is the first initialization
		 */
		initializeFilterValues(isFirst = true) {
			this.filterheadersCopy = this.filterheaders.map(header => {
				// Initialize date range filters
				if (header.type === 'daterange') {
					if (!this.selectedDateRange[header.key]) {
						this.$set(this.selectedDateRange, header.key, []);
					}
					this.$set(this.tempDateRange, header.key, [...(this.selectedDateRange[header.key] || [])]);
				}

				// Initialize single select filters
				if (header.type === 'singleselect' && isFirst) {
					this.$set(this.selectedSingleItem, header.key, header.defaultValue || null);
					const defaultItem = header.defaultValue 
						? header.data.find(item => item[header.value] === header.defaultValue) 
						: null;
					this.$set(this.selectedFilterItemsv2, header.key, defaultItem ? [defaultItem] : []);
					this.$set(this.tempSelectedItems, header.key, defaultItem || null);
				}

				// Filter out null values and 'All' option
				let data = header.data.filter(item => item !== null && item[header.value] !== 'All');

				// Handle default select all for multi-select
				if (header.defaultSelectAll === true && isFirst === true && header.type !== 'singleselect') {
					this.$set(this.selectedFilterItemsv2, header.key, [...data]);
					this.$set(this.tempSelectedItems, header.key, [...data]);
				} else if (header.type !== 'singleselect' && header.type !== 'daterange') {
					// Initialize empty arrays for multi-select if not already set
					if (!this.selectedFilterItemsv2[header.key]) {
						this.$set(this.selectedFilterItemsv2, header.key, []);
					}
					if (!this.tempSelectedItems[header.key]) {
						this.$set(this.tempSelectedItems, header.key, []);
					}
				}

				return { ...header, data };
			});
		},
		isFilterActive(header) {
			if (header.type === 'daterange') {
				return this.selectedDateRange[header.key]?.length > 0;
			}
			return this.selectedFilterItemsv2[header.key]?.length > 0;
		},
		updateDateRange(key, val) {
			// v-date-picker range returns array of dates.
			// We ensure it is sorted so [0] is start and [1] is end.
			if (val && val.length > 1) {
				const sorted = [...val].sort();
				this.$set(this.selectedDateRange, key, sorted);
			}
		},
		/**
		 * Get date range events for highlighting in date picker (saved state)
		 * @param {string} key - Filter header key
		 * @returns {Array} - Array of date strings
		 */
		getRangeEvents(key) {
			const dates = this.selectedDateRange[key];
			if (!dates || dates.length < 2) return [];
			
			const moment = this.$nova.moment;
			if (!moment) return [];

			const start = moment(dates[0]);
			const end = moment(dates[1]);
			const events = [];
			
			let current = start.clone().add(1, 'days');
			while (current.isBefore(end, 'day')) {
				events.push(current.format('YYYY-MM-DD'));
				current.add(1, 'days');
			}
			return events;
		},

		/**
		 * Get temp date range events for highlighting in date picker
		 * @param {string} key - Filter header key
		 * @returns {Array} - Array of date strings
		 */
		getTempRangeEvents(key) {
			const dates = this.tempDateRange[key];
			if (!dates || dates.length < 2) return [];
			
			const moment = this.$nova.moment;
			if (!moment) return [];

			const start = moment(dates[0]);
			const end = moment(dates[1]);
			const events = [];
			
			let current = start.clone().add(1, 'days');
			while (current.isBefore(end, 'day')) {
				events.push(current.format('YYYY-MM-DD'));
				current.add(1, 'days');
			}
			return events;
		},

		/**
		 * Update temporary date range selection
		 * @param {string} key - Filter header key
		 * @param {Array} val - Selected date range
		 */
		updateTempDateRange(key, val) {
			if (val && val.length > 1) {
				const sorted = [...val].sort();
				this.$set(this.tempDateRange, key, sorted);
			}
		},

		handleSingleSelectChange(header) {
			this.selectedFilterItemsv2[header.key] = this.selectedSingleItem[header.key] ? 
				[header.data.find(item => item[header.value] === this.selectedSingleItem[header.key])] : [];
			if (this.searchOption) return;
			this.getFilterData();
		},
		/**
		 * Set the current active header and sync temp state from saved state
		 * @param {string} headerKey - The key of the header being opened
		 */
		setHeader(headerKey) {
			this.selectedHeader = headerKey;
			const header = this.filterheadersCopy.find(h => h.key === headerKey);
			
			if (header) {
				if (header.type === 'daterange') {
					// Sync temp date range from saved state
					this.$set(this.tempDateRange, headerKey, [...(this.selectedDateRange[headerKey] || [])]);
				} else if (header.type === 'singleselect') {
					// Sync temp single select from saved state
					const savedSelection = this.selectedFilterItemsv2[headerKey];
					this.$set(this.tempSelectedItems, headerKey, savedSelection && savedSelection.length > 0 ? savedSelection[0] : null);
				} else {
					// Sync temp multi-select from saved state
					this.$set(this.tempSelectedItems, headerKey, [...(this.selectedFilterItemsv2[headerKey] || [])]);
				}
			}
		},

		/**
		 * Clear temporary selection for a filter
		 * @param {string} key - Filter header key
		 * @param {string} type - Filter type (daterange, singleselect, multiselect)
		 */
		clearTempSelection(key, type) {
			if (type === 'daterange') {
				this.$set(this.tempDateRange, key, []);
				this.$set(this.selectedDateRange, key, []);
			} else if (type === 'singleselect') {
				this.$set(this.tempSelectedItems, key, null);
				this.$set(this.selectedFilterItemsv2, key, []);
			} else {
				this.$set(this.tempSelectedItems, key, []);
				this.$set(this.selectedFilterItemsv2, key, []);
			}
		},

		/**
		 * Save selection from temp state to actual state and close menu
		 * @param {string} key - Filter header key
		 * @param {string} type - Filter type (daterange, singleselect, multiselect)
		 * @param {number} index - Index of the menu ref to close
		 */
		saveSelection(key, type, index) {
			if (type === 'daterange') {
				const tempDates = this.tempDateRange[key];
				// Validation: Ensure both Start and End dates are selected if any date is selected
				if (tempDates && tempDates.length === 1) {
					this.$store.commit("sbError", "Please select both Start and End dates.");
					return;
				}
				this.$set(this.selectedDateRange, key, tempDates ? [...tempDates] : []);
			} else if (type === 'singleselect') {
				const tempValue = this.tempSelectedItems[key];
				this.$set(this.selectedFilterItemsv2, key, tempValue ? [tempValue] : []);
			} else {
				// Multi-select
				const tempValues = this.tempSelectedItems[key];
				this.$set(this.selectedFilterItemsv2, key, tempValues ? [...tempValues] : []);
			}

			// Close the menu
			if (this.$refs.menu && this.$refs.menu[index]) {
				this.$refs.menu[index].isActive = false;
			}

			console.log('[Filter]: Selection saved for', key);
		},

		saveSingleFilter(key, value) {
			if (this.selectedFilterItemsv2[this.selectedHeader]?.[0] === value) {
				this.selectedFilterItemsv2[this.selectedHeader] = [];
			} else {
				this.selectedFilterItemsv2[this.selectedHeader] = [value];
			}
		},
		saveFilter(key, value) {
			// No longer removing items from the list
			// Just ensuring the v-model update is reactive if needed, but v-model handles it.
			// We might need to ensure reactivity if selectedFilterItemsv2[this.selectedHeader] was undefined
			if (!this.selectedFilterItemsv2[this.selectedHeader]) {
				this.$set(this.selectedFilterItemsv2, this.selectedHeader, [value]);
			}
		},
		unSelectallItem(key) {
			this.filterheadersCopy.forEach((item, index) => {
				if (item.key === key) {
					this.filterheadersCopy[index].data.push(this.selectedFilterItemsv2[this.selectedHeader][0]);
					this.selectedFilterItemsv2[this.selectedHeader]= [];
				}
			})
		},
		unSelectSelectedItem(key, value) {
			this.filterheadersCopy.forEach((item, index) => {
				if (item.key === key) {
					let data = [...[value], ...item.data]
					this.filterheadersCopy[index].data = data || []
					this.selectedFilterItemsv2[this.selectedHeader] = this.selectedFilterItemsv2[this.selectedHeader].filter(selectedItem => selectedItem[item.value] !== value[item.value])
					this.filterSearch[this.selectedHeader] = this.filterSearch[this.selectedHeader].filter(selectedItem => selectedItem[item.value] !== value[item.value])
				}
			})
		},
		getFilterOptionsList(searchText = "", data, key) {
			// Create a Set to track unique values (case-insensitive, trimmed)
			const seen = new Set();
			const uniqueData = [];
			for (const item of data) {
				if (!item || item[key] == null || typeof item[key] !== 'string') continue;
				const normalized = item[key].trim().toLowerCase();
				if (!seen.has(normalized)) {
					seen.add(normalized);
					uniqueData.push(item);
				}
			}
			if (searchText !== "") {
				const searchNorm = searchText.trim().toLowerCase();
				return uniqueData.filter(item => typeof item[key] === 'string' && item[key].trim().toLowerCase().includes(searchNorm));
			}
			return uniqueData;
		},
		getSelectedFilterList(searchText = "", data, key) {
			if (searchText !== "") {
				return data.filter(
					item =>
						typeof item[key] === "string" &&
						item[key].toLowerCase().includes(searchText.toLowerCase())
				);
			}
			return data;
		},
		/**
		 * Prepare and emit filter data for API call
		 * Called when Apply button is clicked
		 */
		getFilterData() {
			// Process date range filters
			this.filterheadersCopy.forEach((item) => {
				if (item.type === 'daterange') {
					const dates = this.selectedDateRange[item.key];
					if (dates && dates.length === 2) {
						this.$set(this.selectedFilterItemsv2, item.key, [{ _id: { start: dates[0], end: dates[1] } }]);
					} else {
						this.$delete(this.selectedFilterItemsv2, item.key);
					}
				}
			});

			// Build filter object
			let filter = { ...this.selectedFilterItemsv2 };

			// Clean up filters - remove if all items selected (means no filter)
			this.filterheaders.forEach((item) => {
				if (typeof filter?.[item.key] !== 'undefined') {
					if (item.type === 'singleselect' && filter[item.key].length > 0) {
						// Keep single select filters as-is
					} else if (item.type !== 'daterange' && filter[item.key].length === item.data.length && this.cleanSelectAll) {
						// If all items selected, treat as no filter
						filter[item.key] = [];
					}
				}
			});

			console.log('[Filter]: Applying filters', filter);
			this.$emit('loadFilterData', { filter: filter });
		},
		clearSelections(key) {
			const header = this.filterheaders.find(h => h.key === key);
			if (header?.type === 'singleselect') {
				this.selectedSingleItem[key] = null;
			}
			this.filterheadersCopy.forEach((item, index) => {
				if (item.key === key) {
					this.filterheadersCopy[index].data = this.filterheaders[index]?.data || []
					this.selectedFilterItemsv2[this.selectedHeader] = []
					if(item.type === "daterange"){
						this.selectedDateRange[item.key] = [];
					}
				}
			})
			this.selectedFilterItemsv2[key] = []
		},
		/**
		 * Reset all filters to initial state
		 */
		resetFilters() {
			this.searchFilter = {};
			this.selectedFilterItemsv2 = {};
			this.selectedDateRange = {};
			this.selectedSingleItem = {};
			this.tempSelectedItems = {};
			this.tempDateRange = {};
			
			this.initializeFilterValues(true);
			this.$emit('resetFilters', { filter: {} });
			console.log('[Filter]: All filters reset');
		},
		genarateFile(){
			let filter= {...this.selectedFilterItemsv2};
			this.filterheaders.forEach((item) => {
				if(typeof filter?.[item.key] !== "undefined"){
					if (item.type === 'singleselect' && filter[item.key].length > 0) {
						// Keep single select filters
					} else if (item.type !== "daterange" && filter[item.key].length === item.data.length) {
						filter[item.key] = [];
					}
				}
			})
			this.$emit('genarateFile', { filter: filter })
		},
		clearSingleFilter(key){
			if(key === 'all'){
				this.selectedFilterItemsv2 = {}
				this.searchFilter = {}
				this.selectedSingleItem = {}
				this.initializeFilterValues(true);
			}else{
				const header = this.filterheaders.find(h => h.key === key);
				if (header?.type === 'singleselect') {
					this.selectedSingleItem[key] = null;
				}
				this.filterheadersCopy.forEach((item, index) => {
					if (item.key === key) {
						this.filterheadersCopy[index].data = this.filterheaders[index].data || []
						delete this.searchFilter[this.selectedHeader] 
					}
				})
			}
		},
		clearFilter(key){
			if(key === 'all'){
				this.selectedFilterItemsv2 = {}
				this.searchFilter = {}
				this.selectedSingleItem = {}
				this.initializeFilterValues(true);
			}else{
				const header = this.filterheaders.find(h => h.key === key);
				if (header?.type === 'singleselect') {
					this.selectedSingleItem[key] = null;
				}
				this.filterheadersCopy.forEach((item, index) => {
					if (item.key === key) {
						this.filterheadersCopy[index].data = this.filterheaders[index].data || []
						delete this.selectedFilterItemsv2[this.selectedHeader]
						delete this.searchFilter[this.selectedHeader] 
					}
				})
			}
		}
	},
	watch:{
		filterheaders:{
			handler() {
				this.initializeFilterValues()
			}
		}
	}
}
</script>

<style scoped>
/* Date Picker Range Styling */
::v-deep .v-date-picker-table__events {
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none; /* Ensure clicks pass through to the date button */
}

::v-deep .v-date-picker-table__events > div {
  width: 100%;
  height: 100%;
  border-radius: 0; /* Square shape for range effect */
  margin: 0;
  opacity: 0.6; /* Slight transparency */
}

/* Toolbar container with 8px padding and 84px height */
.filter-toolbar {
  padding: 10px;
  box-shadow: 0px 4px 4px 0px #00000014;
}

.spinner-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50%;
}
.hoverable {
  cursor: pointer;
}
.hoverable:hover {
  background-color: var(--cycle-filter-section-bg) !important;
  border-radius: var(--radius-8);
}
.filter-head{
	line-height: 20px;
}
::v-deep .v-date-picker-table .v-btn.v-btn--active {
  color: black !important;
}
::v-deep .v-picker__title{
	color: black !important;
}
.filter-text{
	color: #828285;
}
</style>